import retry from 'async-retry'
import {
    DocumentData,
    DocumentReference,
    FirestoreDataConverter,
    QueryDocumentSnapshot,
    Timestamp,
    collection,
    deleteDoc,
    doc,
    getDoc,
    onSnapshot,
    setDoc,
} from 'firebase/firestore'

import { firestore } from './firebase.ts'
import { FSDocument } from './types.ts'

export interface GetDocumentParams<T> {
    collectionName: string
    id?: string
    onDocumentChange?: (doc: T) => void
    converter?: FirestoreDataConverter<any, any>
}

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] }

const mapNullToUndefined = <T extends DocumentData>(data: T) =>
    Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: data[key] === null ? undefined : data[key] }), {})

export const getDocumentSnapshot = async <T>(
    docRef: DocumentReference<T>,
): Promise<QueryDocumentSnapshot<T & FSDocument> | undefined> => {
    const snapshot = await retry(
        async bail => {
            try {
                return await getDoc(docRef)
            } catch (error: any) {
                if (error.code === 'unavailable') {
                    throw error // This will trigger a retry
                }
                bail(error) // For other errors, don't retry
            }
        },
        {
            retries: 10,
            minTimeout: 200,
            maxTimeout: 5000,
            onRetry: (err: any, attempt: number) => {
                console.warn(`Retry attempt ${attempt} for document ${docRef.id} in ${docRef.path}:`, err.message)
            },
        },
    )

    if (!snapshot?.exists()) return undefined
    return snapshot as QueryDocumentSnapshot<T & FSDocument>
}

export const getDocument = async <T extends FSDocument>({
    collectionName,
    id,
    converter,
}: WithRequired<GetDocumentParams<T>, 'id'>): Promise<T | undefined> => {
    console.debug('Getting document', collectionName, id)
    const docRef = converter
        ? doc(firestore, collectionName, id).withConverter(converter)
        : doc(firestore, collectionName, id)

    const snapshot = await getDocumentSnapshot(docRef)
    if (!snapshot) return undefined

    const document = { ...mapNullToUndefined(snapshot.data()), id: snapshot.id } as T
    console.debug('Document retrieved', document)
    return document
}

export const listenDocument = async <T extends FSDocument>({
    collectionName,
    id,
    onDocumentChange,
    converter,
}: WithRequired<GetDocumentParams<T>, 'id'>): Promise<{ document: T | undefined; unsubscribe?: () => void }> => {
    // Use the new getDocumentSnapshot function to get the initial document
    const document = await getDocument({ collectionName, id, converter })

    if (!document) return { document: undefined }

    if (onDocumentChange) {
        const docRef = converter
            ? doc(firestore, collectionName, id).withConverter(converter)
            : doc(firestore, collectionName, id)

        const unsubscribe = onSnapshot(docRef, docSnapshot => {
            if (docSnapshot.exists()) {
                onDocumentChange({ ...mapNullToUndefined(docSnapshot.data()), id: docSnapshot.id } as T)
            }
        })

        return { document: document as T, unsubscribe }
    }

    return { document: document as T }
}

export interface SetDocumentParams<T> {
    collectionName: string
    id?: string
    data: T
    converter?: FirestoreDataConverter<any, any>
}

const mapUndefinedToNull = <T extends DocumentData>(data: T) =>
    Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: data[key] === undefined ? null : data[key] }), {})

export const setDocumentByRef = async <T extends DocumentData>(
    docRef: ReturnType<typeof doc>,
    data: T,
    converter?: FirestoreDataConverter<any, any>,
) => {
    await setDoc(
        converter ? docRef.withConverter(converter) : docRef,
        { ...mapUndefinedToNull(data), updatedAt: Timestamp.now() },
        { merge: true },
    )
    const snapshot = await getDocumentSnapshot(docRef)
    if (!snapshot) {
        throw new Error('Document was not created')
    }
    return { ...mapNullToUndefined(snapshot.data()), id: snapshot.id } as T & { id: string }
}

export const setDocument = async <T extends DocumentData>({
    collectionName,
    id,
    data,
    converter,
}: SetDocumentParams<T>): Promise<T & { id: string }> => {
    let docRef: ReturnType<typeof doc>

    if (id) {
        // either the doc exists or we want to create it with a specific id
        docRef = converter
            ? doc(firestore, collectionName, id).withConverter(converter)
            : doc(firestore, collectionName, id)
    } else {
        // the doc does not exist, an id will be autogenerated
        const collectionRef = converter
            ? collection(firestore, collectionName).withConverter(converter)
            : collection(firestore, collectionName)
        docRef = doc(collectionRef)
    }
    return setDocumentByRef(docRef, { ...data, updatedAt: Timestamp.now() }, converter)
}

export interface DeleteDocumentParams {
    collectionName: string
    id: string
    converter?: FirestoreDataConverter<any, any>
}

export const deleteDocument = async ({ collectionName, id, converter }: DeleteDocumentParams): Promise<void> => {
    const docRef = converter
        ? doc(firestore, collectionName, id).withConverter(converter)
        : doc(firestore, collectionName, id)
    await deleteDoc(docRef)
}
